import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ArticleCard from '@/components/feature/ArticleCard';
import ScrollToTop from '@/components/base/ScrollToTop';
import SEOMeta from '@/components/base/SEOMeta';
import JsonLd from '@/components/base/JsonLd';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';

const heroBgImage = 'https://readdy.ai/api/search-image?query=Abstract%20warm%20geometric%20pattern%20with%20soft%20peach%20and%20cream%20tones%2C%20modern%20minimalist%20design%2C%20smooth%20gradient%20texture%2C%20elegant%20and%20clean%20background%2C%20subtle%20organic%20shapes%20floating%20gently%2C%20airy%20atmosphere&width=1600&height=900&seq=hero-bg-mitao&orientation=landscape';

const featuredArticles = articles.slice(0, 6);
const popularArticles = [...articles].sort((a, b) => b.views - a.views).slice(0, 4);
const latestArticles = [...articles].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()).slice(0, 6);

export default function Home() {
  const [newsletterStatus, setNewsletterStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [newsletterError, setNewsletterError] = useState('');
  const [newsletterSubmitting, setNewsletterSubmitting] = useState(false);

  const handleNewsletterSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setNewsletterSubmitting(true);
    setNewsletterError('');

    const form = e.currentTarget;
    const honeypot = form.querySelector<HTMLInputElement>('input[name="phone_alt"]');
    if (honeypot && honeypot.value.trim() !== '') {
      setNewsletterStatus('success');
      setNewsletterSubmitting(false);
      return;
    }

    try {
      const formData = new FormData(form);
      if (honeypot) formData.delete('phone_alt');

      const response = await fetch('https://readdy.ai/api/form/d9u45t5ca23r0tr1aq7g', {
        method: 'POST',
        body: new URLSearchParams(formData as unknown as URLSearchParams).toString(),
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      });

      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      if (response.ok && parsed?.code === 'OK') {
        setNewsletterStatus('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '订阅失败，请稍后重试';
        if (typeof serverMsg === 'string' && serverMsg.toLowerCase().includes('spam')) {
          setNewsletterStatus('success');
        } else {
          setNewsletterError(typeof serverMsg === 'string' ? serverMsg : '订阅失败，请稍后重试');
          setNewsletterStatus('error');
        }
      }
    } catch {
      setNewsletterError('网络错误，请检查网络连接后重试');
      setNewsletterStatus('error');
    } finally {
      setNewsletterSubmitting(false);
    }
  };

  return (
    <>
      <ScrollToTop />
      <SEOMeta
        title="蜜桃网站 - 精选内容聚合平台，海量资源一站直达"
        description="蜜桃网站是您发现优质内容的首选平台，汇聚海量精选资源、热门视频、图文内容与社区互动。蜜桃网站为用户提供流畅、安全、便捷的浏览体验，内容持续更新，满足您多样化的娱乐与资讯需求，立即访问探索更多精彩。"
        keywords="蜜桃网站,蜜桃网站在线入口,蜜桃网站最新地址,蜜桃网站官方平台,蜜桃内容聚合,蜜桃视频资源,蜜桃网站免费观看"
        canonicalUrl="https://weifutongmicropay.cn"
        ogImage="https://readdy.ai/api/search-image?query=Content%20aggregation%20platform%20with%20warm%20modern%20design%2C%20clean%20and%20elegant%20layout%2C%20soft%20peach%20and%20cream%20color%20palette&width=1200&height=630&seq=og-home&orientation=landscape"
      />
      <JsonLd
        data={{
          '@context': 'https://schema.org',
          '@type': 'WebSite',
          name: '蜜桃网站',
          url: 'https://weifutongmicropay.cn',
          description: '蜜桃网站是您发现优质内容的首选平台，汇聚海量精选资源、热门视频、图文内容与社区互动。',
          potentialAction: {
            '@type': 'SearchAction',
            target: 'https://weifutongmicropay.cn/search?q={search_term_string}',
            'query-input': 'required name=search_term_string',
          },
        }}
      />
      <JsonLd
        data={{
          '@context': 'https://schema.org',
          '@type': 'Organization',
          name: '蜜桃网站',
          url: 'https://weifutongmicropay.cn',
          description: '精选内容聚合平台，海量资源一站直达',
          contactPoint: {
            '@type': 'ContactPoint',
            telephone: '400-888-6666',
            contactType: 'customer service',
            availableLanguage: 'Chinese',
          },
        }}
      />

      <Navbar />

      <main>
        {/* Hero Section */}
        <section className="relative w-full h-[420px] md:h-[600px] flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0">
            <img
              src={heroBgImage}
              alt="蜜桃网站精选内容聚合平台"
              className="w-full h-full object-cover"
              title="蜜桃网站 - 精选内容聚合平台"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/35 via-black/20 to-black/45"></div>
          </div>
          <div className="relative z-10 w-full max-w-4xl mx-auto px-4 md:px-6 text-center">
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-heading font-bold text-white mb-4 md:mb-6 leading-tight">
              发现精彩，从这里开始
            </h1>
            <p className="text-sm md:text-lg text-white/85 max-w-2xl mx-auto mb-6 md:mb-8 leading-relaxed">
              蜜桃网站汇集海量精选内容，覆盖科技、娱乐、生活、美食、健康、财经等多元领域。
              每日更新优质文章，为您提供流畅、安全、便捷的阅读体验。
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                to="/category/tech"
                className="w-full sm:w-auto px-8 py-3 bg-primary-500 text-background-50 rounded-full font-medium text-sm hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer"
              >
                开始探索
              </Link>
              <Link
                to="/about"
                className="w-full sm:w-auto px-8 py-3 bg-white/15 backdrop-blur-sm text-white rounded-full font-medium text-sm hover:bg-white/25 transition-all duration-200 whitespace-nowrap cursor-pointer"
              >
                了解更多
              </Link>
            </div>
          </div>
        </section>

        {/* Category Navigation */}
        <section className="py-10 md:py-14 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 text-center mb-8">
              探索内容分类
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4">
              {categories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.slug}`}
                  className="group flex flex-col items-center gap-3 p-5 rounded-lg bg-background-100 hover:bg-primary-50 border border-transparent hover:border-primary-100 transition-all duration-200 cursor-pointer"
                >
                  <span className="w-12 h-12 flex items-center justify-center rounded-full bg-accent-100 text-accent-700 group-hover:bg-primary-500 group-hover:text-background-50 transition-all duration-200 text-xl">
                    <i className={cat.icon}></i>
                  </span>
                  <div className="text-center">
                    <p className="text-sm font-semibold text-foreground-900 whitespace-nowrap">{cat.name}</p>
                    <p className="text-xs text-foreground-500 mt-0.5">{cat.articleCount}篇文章</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Featured Articles */}
        <section className="py-10 md:py-14 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-center justify-between mb-8">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900">
                精选推荐
              </h2>
              <Link
                to="/category/tech"
                className="text-sm text-primary-500 hover:text-primary-600 font-medium transition-colors duration-200 whitespace-nowrap cursor-pointer"
              >
                查看全部 <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {featuredArticles.map((article) => (
                <ArticleCard key={article.id} {...article} />
              ))}
            </div>
          </div>
        </section>

        {/* Popular & Latest */}
        <section className="py-10 md:py-14 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Popular */}
              <div className="lg:col-span-1">
                <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-900 mb-5 flex items-center gap-2">
                  <i className="ri-fire-line text-accent-500"></i>
                  热门排行
                </h2>
                <div className="space-y-4">
                  {popularArticles.map((article, index) => (
                    <Link
                      key={article.id}
                      to={`/article/${article.id}`}
                      className="flex gap-4 group cursor-pointer"
                    >
                      <span className={`text-2xl font-heading font-bold flex-shrink-0 w-8 text-center ${
                        index === 0 ? 'text-accent-500' : index === 1 ? 'text-secondary-500' : 'text-foreground-300'
                      }`}>
                        {String(index + 1).padStart(2, '0')}
                      </span>
                      <div className="min-w-0">
                        <h3 className="text-sm font-medium text-foreground-900 line-clamp-2 group-hover:text-primary-500 transition-colors duration-200 leading-snug">
                          {article.title}
                        </h3>
                        <p className="text-xs text-foreground-500 mt-1 whitespace-nowrap">
                          {article.views >= 10000 ? `${(article.views / 10000).toFixed(1)}万` : article.views} 次阅读
                        </p>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>

              {/* Latest */}
              <div className="lg:col-span-2">
                <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-900 mb-5 flex items-center gap-2">
                  <i className="ri-time-line text-primary-500"></i>
                  最新发布
                </h2>
                <div className="space-y-4">
                  {latestArticles.map((article) => (
                    <Link
                      key={article.id}
                      to={`/article/${article.id}`}
                      className="flex flex-col sm:flex-row gap-4 p-4 rounded-lg hover:bg-background-100 transition-colors duration-200 group cursor-pointer border border-transparent hover:border-background-200/70"
                    >
                      <div className="w-full sm:w-48 h-28 flex-shrink-0 rounded-lg overflow-hidden">
                        <img
                          src={article.imageUrl}
                          alt={article.title}
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                          title={article.title}
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-sm md:text-base font-medium text-foreground-900 line-clamp-2 group-hover:text-primary-500 transition-colors duration-200 leading-snug mb-1.5">
                          {article.title}
                        </h3>
                        <p className="text-xs md:text-sm text-foreground-600 line-clamp-2 mb-2 leading-relaxed">
                          {article.excerpt}
                        </p>
                        <div className="flex items-center gap-3 text-xs text-foreground-500 whitespace-nowrap flex-wrap">
                          <span>{article.date}</span>
                          <span>{article.author}</span>
                          <span className="flex items-center gap-1">
                            <i className="ri-eye-line"></i>
                            {article.views >= 10000 ? `${(article.views / 10000).toFixed(1)}万` : article.views}
                          </span>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Newsletter CTA */}
        <section className="py-14 md:py-20 bg-background-100">
          <div className="max-w-3xl mx-auto px-4 md:px-6 text-center">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-3">
              订阅蜜桃网站精选
            </h2>
            <p className="text-sm text-foreground-600 mb-6 max-w-lg mx-auto leading-relaxed">
              每周精选优质内容推送至您的邮箱，不错过任何精彩文章。免费订阅，随时退订。
            </p>
            <form
              onSubmit={handleNewsletterSubmit}
              className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto"
              data-readdy-form="true"
            >
              <input
                type="email"
                name="email"
                placeholder="输入您的邮箱地址"
                required
                className="flex-1 px-4 py-3 rounded-full border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all duration-200"
              />
              <input
                type="text"
                name="phone_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="sr-only-honeypot"
              />
              <button
                type="submit"
                disabled={newsletterSubmitting}
                className="px-8 py-3 bg-primary-500 text-background-50 rounded-full font-medium text-sm hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 whitespace-nowrap cursor-pointer"
              >
                {newsletterSubmitting ? '提交中...' : '立即订阅'}
              </button>
            </form>
            {newsletterStatus === 'success' && (
              <p className="text-sm text-green-600 font-medium mt-3">感谢订阅！我们会将精选内容发送至您的邮箱。</p>
            )}
            {newsletterStatus === 'error' && (
              <p className="text-sm text-red-600 font-medium mt-3">{newsletterError}</p>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}